import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';

export const getBroadcasts = createAsyncThunk(
    'broadcasts/getBroadcasts',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching broadcasts with params:', params);

            const response = await apiClient.get(API_ENDPOINTS.BROADCASTS, { params });
            console.log('Broadcasts response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Broadcasts retrieved successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch broadcasts',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get broadcasts error:', error);

            let errorMessage = 'Failed to fetch broadcasts';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch broadcasts';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getBroadcast = createAsyncThunk(
    'broadcasts/getBroadcast',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching broadcast:', id);

            const response = await apiClient.get(API_ENDPOINTS.BROADCAST(id));
            console.log('Broadcast response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Broadcast retrieved successfully',
                    broadcast: data?.broadcast || null,
                    sms_logs: data?.sms_logs || []
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch broadcast',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get broadcast error:', error);

            let errorMessage = 'Failed to fetch broadcast';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch broadcast';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getBroadcastSmsLogs = createAsyncThunk(
    'broadcasts/getBroadcastSmsLogs',
    async ({ id, params = {} }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching broadcast SMS logs:', id);

            const response = await apiClient.get(API_ENDPOINTS.BROADCAST_SMS_LOGS(id), { params });
            console.log('Broadcast SMS logs response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Broadcast SMS logs retrieved successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch broadcast SMS logs',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get broadcast SMS logs error:', error);

            let errorMessage = 'Failed to fetch broadcast SMS logs';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch broadcast SMS logs';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const createBroadcast = createAsyncThunk(
    'broadcasts/createBroadcast',
    async (broadcastData, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Creating broadcast...');
            console.log('Broadcast data:', broadcastData);

            const response = await apiClient.post(API_ENDPOINTS.CREATE_BROADCAST, broadcastData);
            console.log('Create broadcast response:', response.data);

            const { status, message, data } = response.data;

            if (status === 201 || status === 200) {
                return {
                    success: true,
                    message: message || 'Broadcast created successfully',
                    broadcast: data?.broadcast || data || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to create broadcast',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Create broadcast error:', error);

            let errorMessage = 'Failed to create broadcast';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to create broadcast';
                
                if (serverData.errors) {
                    const errorMessages = Object.values(serverData.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const updateBroadcast = createAsyncThunk(
    'broadcasts/updateBroadcast',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Updating broadcast:', id);
            console.log('Update data:', data);

            const response = await apiClient.post(API_ENDPOINTS.UPDATE_BROADCAST(id), data);
            console.log('Update broadcast response:', response.data);

            const { status, message, data: responseData } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Broadcast updated successfully',
                    broadcast: responseData?.broadcast || responseData || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to update broadcast',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Update broadcast error:', error);

            let errorMessage = 'Failed to update broadcast';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to update broadcast';
                
                if (serverData.errors) {
                    const errorMessages = Object.values(serverData.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const deleteBroadcast = createAsyncThunk(
    'broadcasts/deleteBroadcast',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Deleting broadcast:', id);

            const response = await apiClient.delete(API_ENDPOINTS.DELETE_BROADCAST(id));
            console.log('Delete broadcast response:', response.data);

            const { status, message } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Broadcast deleted successfully',
                    id: id
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to delete broadcast',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Delete broadcast error:', error);

            let errorMessage = 'Failed to delete broadcast';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to delete broadcast';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const cancelBroadcast = createAsyncThunk(
    'broadcasts/cancelBroadcast',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Cancelling broadcast:', id);

            const response = await apiClient.post(API_ENDPOINTS.CANCEL_BROADCAST(id));
            console.log('Cancel broadcast response:', response.data);

            const { status, message } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Broadcast cancelled successfully',
                    id: id
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to cancel broadcast',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Cancel broadcast error:', error);

            let errorMessage = 'Failed to cancel broadcast';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to cancel broadcast';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const rescheduleBroadcast = createAsyncThunk(
    'broadcasts/rescheduleBroadcast',
    async ({ id, schedule_date }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Rescheduling broadcast:', id);

            const response = await apiClient.post(API_ENDPOINTS.RESCHEDULE_BROADCAST(id), { schedule_date });
            console.log('Reschedule broadcast response:', response.data);

            const { status, message } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Broadcast rescheduled successfully',
                    id: id
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to reschedule broadcast',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Reschedule broadcast error:', error);

            let errorMessage = 'Failed to reschedule broadcast';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to reschedule broadcast';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getBroadcastStats = createAsyncThunk(
    'broadcasts/getStats',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching broadcast statistics...');

            const response = await apiClient.get(API_ENDPOINTS.BROADCAST_STATS, { params });
            console.log('Broadcast stats response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Broadcast statistics retrieved successfully',
                    stats: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch broadcast statistics',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get broadcast stats error:', error);

            let errorMessage = 'Failed to fetch broadcast statistics';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch broadcast statistics';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getTemplates = createAsyncThunk(
    'broadcasts/getTemplates',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching templates...');

            const response = await apiClient.get(API_ENDPOINTS.TEMPLATES, { params });
            console.log('Templates response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Templates retrieved successfully',
                    templates: data || []
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch templates',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get templates error:', error);

            let errorMessage = 'Failed to fetch templates';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch templates';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const createTemplate = createAsyncThunk(
    'broadcasts/createTemplate',
    async (templateData, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Creating template...');

            const response = await apiClient.post(API_ENDPOINTS.CREATE_TEMPLATE, templateData);
            console.log('Create template response:', response.data);

            const { status, message, data } = response.data;

            if (status === 201 || status === 200) {
                return {
                    success: true,
                    message: message || 'Template created successfully',
                    template: data || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to create template',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Create template error:', error);

            let errorMessage = 'Failed to create template';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to create template';
                
                if (serverData.errors) {
                    const errorMessages = Object.values(serverData.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const updateTemplate = createAsyncThunk(
    'broadcasts/updateTemplate',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Updating template:', id);

            const response = await apiClient.post(API_ENDPOINTS.UPDATE_TEMPLATE(id), data);
            console.log('Update template response:', response.data);

            const { status, message, data: responseData } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Template updated successfully',
                    template: responseData || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to update template',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Update template error:', error);

            let errorMessage = 'Failed to update template';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to update template';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const deleteTemplate = createAsyncThunk(
    'broadcasts/deleteTemplate',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Deleting template:', id);

            const response = await apiClient.delete(API_ENDPOINTS.DELETE_TEMPLATE(id));
            console.log('Delete template response:', response.data);

            const { status, message } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Template deleted successfully',
                    id: id
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to delete template',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Delete template error:', error);

            let errorMessage = 'Failed to delete template';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to delete template';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const duplicateTemplate = createAsyncThunk(
    'broadcasts/duplicateTemplate',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Duplicating template:', id);

            const response = await apiClient.post(API_ENDPOINTS.DUPLICATE_TEMPLATE(id));
            console.log('Duplicate template response:', response.data);

            const { status, message, data } = response.data;

            if (status === 201 || status === 200) {
                return {
                    success: true,
                    message: message || 'Template duplicated successfully',
                    template: data || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to duplicate template',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Duplicate template error:', error);

            let errorMessage = 'Failed to duplicate template';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to duplicate template';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

const initialState = {
    broadcasts: [],
    pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
    },
    currentBroadcast: null,
    broadcastSmsLogs: [],
    templates: [],
    stats: null,
    isLoading: false,
    error: null,
    successMessage: null,
    apiMessage: null,
};

const broadcastSlice = createSlice({
    name: 'broadcasts',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccessMessage: (state) => {
            state.successMessage = null;
        },
        clearApiMessage: (state) => {
            state.apiMessage = null;
        },
        clearBroadcastState: (state) => {
            state.broadcasts = [];
            state.pagination = {
                current_page: 1,
                last_page: 1,
                per_page: 15,
                total: 0
            };
            state.currentBroadcast = null;
            state.broadcastSmsLogs = [];
            state.templates = [];
            state.stats = null;
            state.error = null;
            state.successMessage = null;
            state.apiMessage = null;
            state.isLoading = false;
        },
        clearCurrentBroadcast: (state) => {
            state.currentBroadcast = null;
            state.broadcastSmsLogs = [];
        },
    },
    extraReducers: (builder) => {
        builder
            // ===== GET BROADCASTS =====
            .addCase(getBroadcasts.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getBroadcasts.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                const responseData = payload.data || {};
                state.broadcasts = responseData.data || [];
                state.pagination = responseData.meta || {
                    current_page: 1,
                    last_page: 1,
                    per_page: 15,
                    total: 0
                };
                state.successMessage = payload.message || 'Broadcasts retrieved successfully';
                state.apiMessage = payload.message || 'Broadcasts retrieved successfully';
                state.error = null;
            })
            .addCase(getBroadcasts.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch broadcasts';
                state.apiMessage = action.payload?.message || 'Failed to fetch broadcasts';
                state.successMessage = null;
                state.broadcasts = [];
                state.pagination = {
                    current_page: 1,
                    last_page: 1,
                    per_page: 15,
                    total: 0
                };
            })

            // ===== GET BROADCAST =====
            .addCase(getBroadcast.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getBroadcast.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.currentBroadcast = payload.broadcast || null;
                state.broadcastSmsLogs = payload.sms_logs || [];
                state.successMessage = payload.message || 'Broadcast retrieved successfully';
                state.apiMessage = payload.message || 'Broadcast retrieved successfully';
                state.error = null;
            })
            .addCase(getBroadcast.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch broadcast';
                state.apiMessage = action.payload?.message || 'Failed to fetch broadcast';
                state.successMessage = null;
                state.currentBroadcast = null;
                state.broadcastSmsLogs = [];
            })

            // ===== GET BROADCAST SMS LOGS =====
            .addCase(getBroadcastSmsLogs.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getBroadcastSmsLogs.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                const responseData = payload.data || {};
                state.broadcastSmsLogs = responseData.data || [];
                state.successMessage = payload.message || 'Broadcast SMS logs retrieved successfully';
                state.apiMessage = payload.message || 'Broadcast SMS logs retrieved successfully';
                state.error = null;
            })
            .addCase(getBroadcastSmsLogs.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch broadcast SMS logs';
                state.apiMessage = action.payload?.message || 'Failed to fetch broadcast SMS logs';
                state.successMessage = null;
                state.broadcastSmsLogs = [];
            })

            // ===== CREATE BROADCAST =====
            .addCase(createBroadcast.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(createBroadcast.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Broadcast created successfully';
                state.apiMessage = payload.message || 'Broadcast created successfully';
                state.error = null;
                if (payload.broadcast) {
                    state.broadcasts = [payload.broadcast, ...state.broadcasts];
                }
            })
            .addCase(createBroadcast.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to create broadcast';
                state.apiMessage = action.payload?.message || 'Failed to create broadcast';
                state.successMessage = null;
            })

            // ===== UPDATE BROADCAST =====
            .addCase(updateBroadcast.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(updateBroadcast.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Broadcast updated successfully';
                state.apiMessage = payload.message || 'Broadcast updated successfully';
                state.error = null;
                if (payload.broadcast) {
                    state.broadcasts = state.broadcasts.map(b => 
                        b.id === payload.broadcast.id ? payload.broadcast : b
                    );
                    if (state.currentBroadcast?.id === payload.broadcast.id) {
                        state.currentBroadcast = payload.broadcast;
                    }
                }
            })
            .addCase(updateBroadcast.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to update broadcast';
                state.apiMessage = action.payload?.message || 'Failed to update broadcast';
                state.successMessage = null;
            })

            // ===== DELETE BROADCAST =====
            .addCase(deleteBroadcast.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(deleteBroadcast.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Broadcast deleted successfully';
                state.apiMessage = payload.message || 'Broadcast deleted successfully';
                state.error = null;
                if (payload.id) {
                    state.broadcasts = state.broadcasts.filter(b => b.id !== payload.id);
                    if (state.currentBroadcast?.id === payload.id) {
                        state.currentBroadcast = null;
                        state.broadcastSmsLogs = [];
                    }
                }
            })
            .addCase(deleteBroadcast.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to delete broadcast';
                state.apiMessage = action.payload?.message || 'Failed to delete broadcast';
                state.successMessage = null;
            })

            // ===== CANCEL BROADCAST =====
            .addCase(cancelBroadcast.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(cancelBroadcast.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Broadcast cancelled successfully';
                state.apiMessage = payload.message || 'Broadcast cancelled successfully';
                state.error = null;
                if (payload.id) {
                    state.broadcasts = state.broadcasts.map(b => 
                        b.id === payload.id ? { ...b, status: 'cancelled' } : b
                    );
                    if (state.currentBroadcast?.id === payload.id) {
                        state.currentBroadcast = { ...state.currentBroadcast, status: 'cancelled' };
                    }
                }
            })
            .addCase(cancelBroadcast.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to cancel broadcast';
                state.apiMessage = action.payload?.message || 'Failed to cancel broadcast';
                state.successMessage = null;
            })

            // ===== RESCHEDULE BROADCAST =====
            .addCase(rescheduleBroadcast.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(rescheduleBroadcast.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Broadcast rescheduled successfully';
                state.apiMessage = payload.message || 'Broadcast rescheduled successfully';
                state.error = null;
                if (payload.id) {
                    state.broadcasts = state.broadcasts.map(b => 
                        b.id === payload.id ? { ...b, status: 'scheduled' } : b
                    );
                    if (state.currentBroadcast?.id === payload.id) {
                        state.currentBroadcast = { ...state.currentBroadcast, status: 'scheduled' };
                    }
                }
            })
            .addCase(rescheduleBroadcast.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to reschedule broadcast';
                state.apiMessage = action.payload?.message || 'Failed to reschedule broadcast';
                state.successMessage = null;
            })

            // ===== GET BROADCAST STATS =====
            .addCase(getBroadcastStats.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getBroadcastStats.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.stats = payload.stats || null;
                state.successMessage = payload.message || 'Broadcast statistics retrieved successfully';
                state.apiMessage = payload.message || 'Broadcast statistics retrieved successfully';
                state.error = null;
            })
            .addCase(getBroadcastStats.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch broadcast statistics';
                state.apiMessage = action.payload?.message || 'Failed to fetch broadcast statistics';
                state.successMessage = null;
                state.stats = null;
            })

            // ===== GET TEMPLATES =====
            .addCase(getTemplates.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getTemplates.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.templates = payload.templates || [];
                state.successMessage = payload.message || 'Templates retrieved successfully';
                state.apiMessage = payload.message || 'Templates retrieved successfully';
                state.error = null;
            })
            .addCase(getTemplates.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch templates';
                state.apiMessage = action.payload?.message || 'Failed to fetch templates';
                state.successMessage = null;
                state.templates = [];
            })

            // ===== CREATE TEMPLATE =====
            .addCase(createTemplate.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(createTemplate.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Template created successfully';
                state.apiMessage = payload.message || 'Template created successfully';
                state.error = null;
                if (payload.template) {
                    state.templates = [payload.template, ...state.templates];
                }
            })
            .addCase(createTemplate.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to create template';
                state.apiMessage = action.payload?.message || 'Failed to create template';
                state.successMessage = null;
            })

            // ===== UPDATE TEMPLATE =====
            .addCase(updateTemplate.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(updateTemplate.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Template updated successfully';
                state.apiMessage = payload.message || 'Template updated successfully';
                state.error = null;
                if (payload.template) {
                    state.templates = state.templates.map(t => 
                        t.id === payload.template.id ? payload.template : t
                    );
                }
            })
            .addCase(updateTemplate.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to update template';
                state.apiMessage = action.payload?.message || 'Failed to update template';
                state.successMessage = null;
            })

            // ===== DELETE TEMPLATE =====
            .addCase(deleteTemplate.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(deleteTemplate.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Template deleted successfully';
                state.apiMessage = payload.message || 'Template deleted successfully';
                state.error = null;
                if (payload.id) {
                    state.templates = state.templates.filter(t => t.id !== payload.id);
                }
            })
            .addCase(deleteTemplate.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to delete template';
                state.apiMessage = action.payload?.message || 'Failed to delete template';
                state.successMessage = null;
            })

            // ===== DUPLICATE TEMPLATE =====
            .addCase(duplicateTemplate.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(duplicateTemplate.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Template duplicated successfully';
                state.apiMessage = payload.message || 'Template duplicated successfully';
                state.error = null;
                if (payload.template) {
                    state.templates = [payload.template, ...state.templates];
                }
            })
            .addCase(duplicateTemplate.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to duplicate template';
                state.apiMessage = action.payload?.message || 'Failed to duplicate template';
                state.successMessage = null;
            });
    },
});

export const {
    clearError,
    clearSuccessMessage,
    clearApiMessage,
    clearBroadcastState,
    clearCurrentBroadcast,
} = broadcastSlice.actions;

export const selectBroadcasts = (state) => state.broadcasts?.broadcasts || [];
export const selectBroadcastPagination = (state) => state.broadcasts?.pagination || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
};
export const selectCurrentBroadcast = (state) => state.broadcasts?.currentBroadcast || null;
export const selectBroadcastSmsLogs = (state) => state.broadcasts?.broadcastSmsLogs || [];
export const selectTemplates = (state) => state.broadcasts?.templates || [];
export const selectBroadcastStats = (state) => state.broadcasts?.stats || null;
export const selectBroadcastsLoading = (state) => state.broadcasts?.isLoading || false;
export const selectBroadcastsError = (state) => state.broadcasts?.error || null;
export const selectBroadcastsSuccess = (state) => state.broadcasts?.successMessage || null;
export const selectBroadcastsApiMessage = (state) => state.broadcasts?.apiMessage || null;

export const selectBroadcastStatsSummary = (state) => {
    const stats = selectBroadcastStats(state);
    if (!stats) return null;
    return {
        total: stats.total || 0,
        sent: stats.sent || 0,
        scheduled: stats.scheduled || 0,
        drafts: stats.drafts || 0,
        failed: stats.failed || 0,
        cancelled: stats.cancelled || 0,
        recent: stats.recent || []
    };
};

export default broadcastSlice.reducer;